import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Copy, Check, Mail } from "lucide-react";
import { API, fmtErr, fmtDate } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { StatusBadge } from "../../components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

export default function AdminShops() {
  const { t } = useLang();
  const [shops, setShops] = useState(null);
  const [plans, setPlans] = useState([]);
  const [resetShop, setResetShop] = useState(null);
  const [tempPw, setTempPw] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => API.get("/admin/shops").then((r) => setShops(r.data)).catch(() => setShops([]));
  useEffect(() => {
    load();
    API.get("/admin/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  if (!shops) return <p className="text-slate-500">{t("common.loading")}</p>;

  const patch = async (shop, body) => {
    try {
      await API.patch(`/admin/shops/${shop.id}`, body);
      toast.success(t("msg.saved"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const removeShop = async (s) => {
    if (!window.confirm(`${s.name}: ${t("admin.delete_shop_confirm")}`)) return;
    try {
      await API.delete(`/admin/shops/${s.id}`);
      toast.success(t("msg.deleted"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const openReset = (s) => {
    setResetShop(s);
    setTempPw(null);
    setCopied(false);
  };

  const genTemp = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/admin/shops/${resetShop.id}/reset-password`);
      setTempPw(data.temp_password);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async () => {
    setBusy(true);
    try {
      await API.post(`/admin/shops/${resetShop.id}/send-reset-email`);
      toast.success(t("admin.reset_email_sent"));
      setResetShop(null);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const copyPw = async () => {
    try {
      await navigator.clipboard.writeText(tempPw);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* ignore */ }
  };

  return (
    <div data-testid="admin-shops-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 mb-6">{t("nav.shops")}</h1>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("admin.shop")}</TableHead>
              <TableHead>{t("admin.owner")}</TableHead>
              <TableHead>{t("landing.plan")}</TableHead>
              <TableHead>{t("settings.period_end")}</TableHead>
              <TableHead>{t("admin.created")}</TableHead>
              <TableHead>{t("common.status")}</TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shops.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">{t("common.none")}</TableCell></TableRow>
            )}
            {shops.map((s) => (
              <TableRow key={s.id} data-testid={`admin-shop-row-${s.slug}`}>
                <TableCell>
                  <div className="font-medium">{s.name}</div>
                  <div className="font-mono text-xs text-slate-500">{s.slug}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{s.owner_name || "—"}</div>
                  <div className="text-xs text-slate-500">{s.owner_email}</div>
                </TableCell>
                <TableCell>
                  <Select value={s.plan_id} onValueChange={(v) => patch(s, { plan_id: v })}>
                    <SelectTrigger data-testid={`shop-plan-select-${s.slug}`} className="w-36 h-8 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-sm">{s.current_period_end ? fmtDate(s.current_period_end) : "—"}</TableCell>
                <TableCell className="text-sm">{fmtDate(s.created_at)}</TableCell>
                <TableCell><StatusBadge status={s.status} /></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button data-testid={`reset-pw-${s.slug}`} onClick={() => openReset(s)}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
                    <KeyRound className="h-3.5 w-3.5" />{t("admin.reset_pw")}
                  </button>
                  {s.status === "suspended" ? (
                    <button data-testid={`activate-shop-${s.slug}`} onClick={() => patch(s, { status: "active" })}
                      className="ml-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">
                      {t("admin.activate")}
                    </button>
                  ) : (
                    <button data-testid={`suspend-shop-${s.slug}`} onClick={() => patch(s, { status: "suspended" })}
                      className="ml-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50">
                      {t("admin.suspend")}
                    </button>
                  )}
                  <button data-testid={`delete-shop-${s.slug}`} onClick={() => removeShop(s)}
                    className="ml-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700">
                    {t("common.delete")}
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!resetShop} onOpenChange={(o) => !o && setResetShop(null)}>
        <DialogContent className="bg-white" data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-red-600" />{t("admin.reset_pw_title")}</DialogTitle>
            <DialogDescription>
              {t("admin.reset_pw_desc")}
              {resetShop && <span className="block mt-1 font-medium text-slate-700">{resetShop.name} · {resetShop.owner_email}</span>}
            </DialogDescription>
          </DialogHeader>

          {tempPw ? (
            <div className="space-y-3" data-testid="temp-pw-result">
              <div className="flex items-center gap-2 bg-slate-900 rounded-lg px-4 py-3">
                <code className="flex-1 font-mono text-lg text-amber-300 tracking-wide" data-testid="temp-pw-value">{tempPw}</code>
                <button onClick={copyPw} data-testid="copy-temp-pw"
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-white/10 text-white hover:bg-white/20">
                  {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                  {copied ? t("admin.copied") : t("admin.copy")}
                </button>
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">{t("admin.temp_pw_note")}</p>
            </div>
          ) : (
            <div className="space-y-2 pt-1">
              <button onClick={genTemp} disabled={busy} data-testid="gen-temp-pw-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50">
                <KeyRound className="h-4 w-4" />{t("admin.gen_temp")}
              </button>
              <button onClick={sendEmail} disabled={busy} data-testid="send-reset-email-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 disabled:opacity-50">
                <Mail className="h-4 w-4" />{t("admin.send_reset_email")}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
